import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { updateJob } from '../../redux/slices/jobScheduleSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import useScheduleChangeLog from '../../functions/useScheduleChangeLog';
import { patchData } from '../../redux/slices/apiCallSlice';

const AccountingModal = ({ show, onHide, job }) => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const { logScheduleChange } = useScheduleChangeLog();
  
  // Initialize state with job data
  const [formData, setFormData] = useState({
    DollarValue: '',
    PaymentTerms: ''
  });
  
  // Update form data when job changes or modal opens
  useEffect(() => {
    if (job && show) {
      setFormData({
        DollarValue: job.DollarValue || '',
        PaymentTerms: job.PaymentTerms || ''
      });
    }
  }, [job, show]);
  
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    
    // Validate DollarValue if provided
    if (formData.DollarValue && isNaN(parseFloat(formData.DollarValue))) {
      newErrors.DollarValue = 'Dollar Value must be a valid number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save changes
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    
    try {
      const updateData = {
        ID: job.ID,
        DollarValue: formData.DollarValue || null,
        PaymentTerms: formData.PaymentTerms || null
      };

      // Make the PATCH request to save changes to the server
      const responseData = await dispatch(
        patchData({
          url: `${apiUrls.generalProject}/?id=${job.ID}`,
          data: updateData,
        })
      ).unwrap();

      if (responseData) {
        console.log('Accounting data updated successfully:', responseData);

        // Log changes for fields that were modified
        const originalData = {
          DollarValue: job.DollarValue,
          PaymentTerms: job.PaymentTerms
        };

        Object.keys(updateData).forEach(field => {
          if (field !== 'ID' && updateData[field] !== originalData[field]) {
            logScheduleChange(
              job.ID,
              job.JobNumber,
              field,
              originalData[field],
              updateData[field]
            );
          }
        });

        // Update job in Redux store
        dispatch(updateJob(updateData));

        onHide();
      }
    } catch (error) {
      console.error('Error saving accounting data:', error);
      setErrors({ general: 'Failed to save changes. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (!value) return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  return (
    <Modal className="job-schedule-modal" show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Accounting Information</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {errors.general && (
          <div className="alert alert-danger" role="alert">
            {errors.general}
          </div>
        )}
        
        <Form>
          {/* Read-only fields */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Job Number</Form.Label>
                <Form.Control
                  type="text"
                  value={job?.JobNumber || ''}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Project Name</Form.Label>
                <Form.Control
                  type="text"
                  value={job?.ProjectName || ''}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Model Number</Form.Label>
                <Form.Control
                  type="text"
                  value={job?.ModelNumber || ''}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Editable fields */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Dollar Value</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.DollarValue}
                  onChange={(e) => handleInputChange('DollarValue', e.target.value)}
                  placeholder="Enter dollar value"
                  isInvalid={!!errors.DollarValue}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.DollarValue}
                </Form.Control.Feedback>
                {formData.DollarValue && !errors.DollarValue && (
                  <Form.Text className="text-muted">
                    Formatted: {formatCurrency(formData.DollarValue)}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Payment Terms</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.PaymentTerms}
                  onChange={(e) => handleInputChange('PaymentTerms', e.target.value)}
                  placeholder="Enter payment terms"
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={onHide}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AccountingModal;
